import {
  setData,
  restForm,
  batchCreationSuccess,
  batchCreationFailure,
  setUpdatedLogDocumentId,
} from 'reducer/trainingSlice';
import rootAxios from 'requests/rootAxios';
import { createAsyncThunk } from '@reduxjs/toolkit';

const Base_url = process.env.REACT_APP_BASE_URL;

export const createBatch = () => async (dispatch, getState) => {
  const trainingFormDetail = getState().trainingSlice;
  const {
    address,
    batchId,
    batchTrainees,
    batchType,
    description,
    duration,
    branch,
    trainingLink,
    trainingType,
    region,
    dates,
    datesEnd,
    googleMapsLink,
    emailConfig,
    trainingRoLocation,
  } = trainingFormDetail;

  let payload = {
    address,
    batchId,
    batchTrainees,
    batchType,
    description,
    duration,
    branch,
    trainingLink,
    trainingType,
    region,
    dates,
    datesEnd,
    googleMapsLink,
    emailConfig,
    trainingRoLocation,
  };
  try {
    const response = await rootAxios.post(`${Base_url}/user/createBatch`, {
      trainingFormDetail: payload,
    });
    dispatch(batchCreationSuccess(true));
    return response.data;
  } catch (error) {
    dispatch(batchCreationFailure(error.message));
    throw error;
  }
};

export const updateBatch =
  ({ updateReason, selectedFile }) =>
  async (dispatch, getState) => {
    const trainingFormDetail = getState().trainingSlice;
    const {
      address,
      batchId,
      batchTrainees,
      batchType,
      description,
      duration,
      branch,
      trainingLink,
      trainingType,
      region,
      dates,
      datesEnd,
      googleMapsLink,
      trainingRoLocation,
    } = trainingFormDetail;

    let payload = {
      address,
      batchId,
      batchTrainees,
      batchType,
      description,
      duration,
      branch,
      trainingLink,
      trainingType,
      region,
      dates,
      datesEnd,
      googleMapsLink,
      trainingRoLocation,
    };

    try {
      const formData = new FormData();
      formData.append('trainingFormDetail', JSON.stringify(payload));
      formData.append('updateReason', updateReason);
      formData.append('shouldStopMail', 'true');
      if (selectedFile) {
        formData.append('selectedFile', selectedFile);
      }

      const response = await rootAxios.put(`${Base_url}/user/updateBatch`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      dispatch(batchCreationSuccess(true));
      // set Updated log document id, we wil update reasons on that .
      let updatedLogDocumentId = response.data.data._id;
      console.log('updatedLogDocumentId', updatedLogDocumentId);
      dispatch(setUpdatedLogDocumentId(updatedLogDocumentId));
      return response.data;
    } catch (error) {
      dispatch(batchCreationFailure(error.message));
      throw error;
    }
  };

export const updateBatchReason = (formData) => async (dispatch) => {
  try {
    console.log('formData', formData);
    const response = await rootAxios.post(`${Base_url}/user/updateBatchReason`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchRegions = createAsyncThunk(
  'training/fetchRegions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await rootAxios.get(`${Base_url}/user/getRegion`);
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Store the current AbortController for request cancellation
let currentAbortController = null;

export const fetchDailyEmployeesWhoJoined = createAsyncThunk(
  'training/fetchDailyEmployeesWhoJoined',
  async (payload, { rejectWithValue, signal }) => {
    try {
      // Cancel previous request if it exists
      if (currentAbortController) {
        currentAbortController.abort();
      }

      // Create new AbortController for this request
      currentAbortController = new AbortController();

      const response = await rootAxios.post(`/user/getLastestEmployees`, payload, {
        params: {
          skip: 0,
          limit: 1000,
        },
        signal: currentAbortController.signal,
      });

      return response.data.data;
    } catch (error) {
      // Don't reject if the request was aborted
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        return rejectWithValue('Request was cancelled');
      }
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchBatchData = createAsyncThunk(
  'training/fetchBatchData',
  async (payload, { rejectWithValue }) => {
    const { batchId, dispatch } = payload;

    try {
      const response = await rootAxios.post(`${Base_url}/user/getBatch`, {
        batchId,
      });

      let regionArray = response?.data?.data?.batchData[0].region;
      let regions = regionArray.map((item) => item.id);
      dispatch(fetchRegionsBranches({ regions: regions }));

      const batchData = response?.data?.data?.batchData[0];

      // Transform email config if it exists in the response
      let emailConfig = {
        totalSteps: 3,
        steps: [
          { to: [], cc: [] },
          { to: [], cc: [] },
          { to: [], cc: [] },
        ],
      };

      if (batchData?.emailConfig) {
        emailConfig = {
          totalSteps: batchData.emailConfig.totalSteps || 3,
          steps: batchData.emailConfig.steps.map((step) => ({
            to: step.to || [],
            cc: step.cc || [],
          })),
        };
      }

      // Handle trainingRoLocation
      let trainingRoLocation = null;
      if (batchData?.trainingRoLocation) {
        // If it's just a string (label), find the matching region object
        const matchingRegion = regionArray.find(
          (region) => region.label === batchData.trainingRoLocation
        );
        if (matchingRegion) {
          trainingRoLocation = matchingRegion;
        } else {
          // If no match found, create a simple object with just the label
          trainingRoLocation = { label: batchData.trainingRoLocation };
        }
      }

      return {
        ...batchData,
        emailConfig,
        trainingRoLocation,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchSuperUserRegions = createAsyncThunk(
  'training/fetchSuperuserRegions',
  async (_, { rejectWithValue }) => {
    try {
      let regionsToReturn = [];
      const response = await rootAxios.get(`${Base_url}/super-user/getAllRegions`);
      // response?.data?.data?.regions.forEach(regionElement=>{
      //   regionsToReturn.push(`${regionElement.regionId}-${regionElement.regionName}`)
      // })
      return response?.data?.data?.regions;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchAllTrainers = createAsyncThunk(
  'training/fetchAllTrainers',
  async ({ skip, limit }, { rejectWithValue }) => {
    try {
      const response = await rootAxios.get(
        `${Base_url}/super-user/getTrainers?skip=${skip}&limit=${limit}`
      );
      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchAllTrainingTypes = createAsyncThunk(
  'training/fetchAllTrainingTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await rootAxios.get(`${Base_url}/super-user/get-training-types`);
      return response?.data?.data?.trainingTypes;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchRegionsBranches = createAsyncThunk(
  'training/fetchRegionsBranches',
  async (regions, { rejectWithValue }) => {
    try {
      const response = await rootAxios.post(`${Base_url}/user/getRegionBatches`, regions);
      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchAllAvaiableTraineesForApproval = createAsyncThunk(
  'training/fetchAllAvaiableTraineesForApproval',
  async ({ batchId, role, type }, { rejectWithValue }) => {
    try {
      console.log('batchId,role', batchId, role);
      const response = await rootAxios.post(`${Base_url}/user/get-approval-trainees`, {
        batchId,
        role,
        type,
      });
      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const submitTraineeStatus = createAsyncThunk(
  'training/submitTraineeStatus',

  async ({ batchId, step, payload }, { rejectWithValue }) => {
    try {
      const response = await rootAxios.post(`${Base_url}/user/approve-trainees`, {
        batchId,
        step,
        payload,
      });
      return response?.data?.data?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateTrainingTask = createAsyncThunk(
  'training/updateTrainingTask',
  async ({ approvalAuthority, batchId, step }, { rejectWithValue }) => {
    try {
      const response = await rootAxios.post(`${Base_url}/user/update-training-task`, {
        approvalAuthority,
        batchId,
        step,
      });
      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchAllBatches = createAsyncThunk(
  'training/fetchAllBatches',
  async (_, { rejectWithValue }) => {
    try {
      const response = await rootAxios.get(`${Base_url}/user/getAllBatches`);
      return response?.data?.data?.listOfBatches;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);
